import { useMutation } from '@tanstack/react-query';
import { useAuthenticatedFetch } from './useAuth';
import type {
  KnowledgeRun,
  KnowledgeSource,
} from '../domains/compass/models/KnowledgeIngestion';

export interface SourceLineageSuggestion {
  source: KnowledgeSource;
  completedRun: KnowledgeRun;
  reasons: string[];
  score: number;
}

interface SourceLineageSuggestionInput {
  folderId: string;
  contentHash?: string | null;
  fileName?: string | null;
  documentTitle?: string | null;
  documentVersion?: string | null;
}

interface SourceLineageSuggestionResponse {
  suggestions?: SourceLineageSuggestion[];
  error?: string;
}

export function useSourceLineageSuggestionsMutation() {
  const fetcher = useAuthenticatedFetch();

  return useMutation({
    mutationFn: async (input: SourceLineageSuggestionInput) => {
      const params = new URLSearchParams();
      appendParam(params, 'contentHash', input.contentHash);
      appendParam(params, 'fileName', input.fileName);
      appendParam(params, 'documentTitle', input.documentTitle);
      appendParam(params, 'documentVersion', input.documentVersion);

      const response = await fetcher(
        `/api/compass/folders/${encodeURIComponent(input.folderId)}/source-lineage/suggestions?${params.toString()}`,
      );
      const payload = (await response
        .json()
        .catch(() => ({}))) as SourceLineageSuggestionResponse;
      if (!response.ok) {
        throw new Error(
          payload.error ?? 'Failed to check prior source lineage',
        );
      }
      return payload.suggestions ?? [];
    },
  });
}

function appendParam(
  params: URLSearchParams,
  key: string,
  value: string | null | undefined,
): void {
  const trimmed = value?.trim();
  if (trimmed) params.set(key, trimmed);
}
